import { computed, DestroyRef, inject, Injectable, Injector, runInInjectionContext, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import {
  getExhibitionSettings,
  getProfileBillingAddress,
  isUserLoggedIn,
  selectIsGoogleMapsApiEnabled,
  selectIsLogoutProcessing
} from '@app/app.reducer';
import {
  DynamicFormField,
  DynamicFormFields,
  FieldKey,
  OptionKey
} from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { FormFactory } from '@core/data-access/models/form/form-mediator.model';
import { CheckboxFormField } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { DynamicFieldOptionsTranslationFactory } from '@core/data-access/services/dynamic-form-field/dynamic-field-options-translation-factory.service';
import { DynamicFormFactory } from '@core/data-access/services/dynamic-form-field/dynamic-form-factory.service';
import { DynamicFormFieldBuilder } from '@core/data-access/services/dynamic-form-field/dynamic-form-field-builder.service';
import {
  BuyerBillingAddressFormState,
  BuyerBillingAddressFormViewModel,
  SavedBillingAddresses
} from '@core/features/buyer-information/models/buyer-billing-address-form.model';
import {
  selectBuyerBillingAddressFormFields,
  selectBuyerInformationFormFields,
  selectBuyerPerson,
  selectSelectedSavedBillingAddressId
} from '@core/features/buyer-information/store/buyer-information.selectors';
import { buyerBillingAddressFormDynamicFormFieldParametersMapper } from '@core/utils/dynamic-form-field/dynamic-form-field-parameters-mapper';
import { filterChangeFormFieldsPipe, setChangedFormFields } from '@core/utils/form-fields/form-field-pipes';
import { select, Store } from '@ngrx/store';
import {
  ExhibitionSettingModel,
  FieldSettings
} from '@shared/services-with-reducers/customization/customization.interfaces';
import { filter as _filter, find, forIn, intersection, isEmpty, isNil, join, keys, reduce } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilKeyChanged,
  filter,
  first,
  map,
  merge,
  Observable,
  skip,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BuyerBillingAddressFormFactory implements FormFactory {
  readonly #injector = inject(Injector);
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(Store);
  readonly #dynamicFormFieldBuilder = inject(DynamicFormFieldBuilder);
  readonly #dynamicFormFactory = inject(DynamicFormFactory);
  readonly #dynamicFieldOptionsTranslationFactory = inject(DynamicFieldOptionsTranslationFactory);

  readonly #initialState = {
    viewModel: null,
    dynamicFormFields: [],
    form: {
      formName: FormName.BuyerBillingAddress,
      formGroup: null
    },
    person: null
  };

  readonly #resetFormState$ = new BehaviorSubject(false);

  readonly #buyerPerson$ = this.#store.select(selectBuyerPerson);
  readonly #buyerBillingAddressFormFields$ = this.#store.select(selectBuyerBillingAddressFormFields);
  readonly #isUseDifferentBillingAddressChecked$ = this.setIsUseDifferentBillingAddressChecked$();
  readonly #savedBillingAddresses$ = this.setSavedBillingAddresses$();

  readonly #isFormStateCreated = signal(false);

  readonly state = toSignal(this.state$(), {
    initialValue: this.#initialState
  });

  resetFormState() {
    this.#resetFormState$.next(true);
  }

  private state$(): Observable<BuyerBillingAddressFormState> {
    return merge(this.createFormState$(), this.updateFormState$(), this.resetFormState$()).pipe(
      takeUntilDestroyed(this.#destroyRef)
    );
  }

  private createFormState$() {
    return combineLatest({
      resetFormState: this.#resetFormState$,
      person: this.#buyerPerson$,
      isUseDifferentBillingAddressChecked: this.#isUseDifferentBillingAddressChecked$
    }).pipe(
      filter(
        ({ resetFormState, isUseDifferentBillingAddressChecked }) =>
          !resetFormState && !this.#isFormStateCreated() && isUseDifferentBillingAddressChecked
      ),
      withLatestFrom(this.#buyerBillingAddressFormFields$, this.#savedBillingAddresses$),
      switchMap(([{ person }, formFields, savedBillingAddresses]) =>
        this.createBillingAddressDynamicFormFields$().pipe(
          tap((dynamicFormFields) =>
            this.#dynamicFieldOptionsTranslationFactory.createTranslatedDynamicFieldOptions(
              FormName.BuyerBillingAddress,
              dynamicFormFields,
              true
            )
          ),
          map((dynamicFormFields) => ({
            viewModel: this.setViewModel(savedBillingAddresses)(),
            dynamicFormFields,
            form: {
              formName: FormName.BuyerBillingAddress,
              formGroup: this.#dynamicFormFactory.createForm(dynamicFormFields, formFields)
            },
            person
          })),
          tap(() => this.#isFormStateCreated.set(true))
        )
      )
    );
  }

  private updateFormState$() {
    return toObservable(this.#isFormStateCreated).pipe(
      filter((isFormStateCreated) => isFormStateCreated),
      switchMap(() =>
        merge(
          this.updateFormStateOnFormFieldsChange$(),
          this.updateFormStateOnLoggedInUserAndSavedBillingAddressesChange$(),
          this.updateFormStateOnBuyerPersonIdChange$()
        )
      )
    );
  }

  private updateFormStateOnFormFieldsChange$() {
    return this.#buyerBillingAddressFormFields$.pipe(
      filterChangeFormFieldsPipe(),
      filter((formFields) => !isEmpty(formFields) && this.#isFormStateCreated()),
      map((formFields) => {
        const { viewModel, dynamicFormFields, form, person } = this.state();

        return {
          viewModel,
          dynamicFormFields: this.#dynamicFormFactory.updateForm(dynamicFormFields, formFields, form.formGroup),
          form,
          person
        };
      })
    );
  }

  private updateFormStateOnLoggedInUserAndSavedBillingAddressesChange$() {
    return combineLatest({
      isUserLoggedIn: this.#store.select(isUserLoggedIn),
      savedBillingAddresses: this.#savedBillingAddresses$
    }).pipe(
      filter(({ isUserLoggedIn }) => this.#isFormStateCreated() && isUserLoggedIn),
      map(({ isUserLoggedIn, savedBillingAddresses }) => {
        const { dynamicFormFields, form, person } = this.state();

        return {
          viewModel: this.setViewModel(savedBillingAddresses)(),
          dynamicFormFields: this.setBillingAddressesFieldsVisibility(
            dynamicFormFields,
            form.formGroup,
            isUserLoggedIn
          ),
          form,
          person
        };
      })
    );
  }

  private updateFormStateOnBuyerPersonIdChange$() {
    const skipFirstLoad = 1;

    return this.#buyerPerson$.pipe(
      filter((person) => this.#isFormStateCreated() && !isNil(person)),
      distinctUntilKeyChanged('id'),
      skip(skipFirstLoad),
      map((person) => {
        const { viewModel, dynamicFormFields, form } = this.state();

        return {
          viewModel,
          dynamicFormFields,
          form,
          person
        };
      })
    );
  }

  private resetFormState$() {
    const isDifferentBillingAddressUnchecked$ = this.#isUseDifferentBillingAddressChecked$.pipe(
      map((isUseDifferentBillingAddressChecked) => !isUseDifferentBillingAddressChecked)
    );

    return merge(this.#resetFormState$, isDifferentBillingAddressUnchecked$).pipe(
      withLatestFrom(this.#store.select(selectIsLogoutProcessing)),
      filter(([resetFormState, isLogoutProcessing]) => resetFormState && !isLogoutProcessing),
      map(() => this.#initialState),
      tap(() => {
        this.#isFormStateCreated.set(false);

        this.#resetFormState$.next(false);
      })
    );
  }

  private setIsUseDifferentBillingAddressChecked$() {
    return this.#store.pipe(
      select(selectBuyerInformationFormFields),
      map((buyerInformationFormFields) => {
        const changedFormFields = setChangedFormFields([{}, buyerInformationFormFields]);

        const isDifferentBillingAddressChanged =
          !isEmpty(changedFormFields) &&
          !isEmpty(intersection(keys(changedFormFields), [FieldKey.DifferentBillingAddress]));

        if (!isDifferentBillingAddressChanged) {
          return false;
        }

        const differentBillingAddressFormField: CheckboxFormField = changedFormFields[FieldKey.DifferentBillingAddress];

        return !!differentBillingAddressFormField?.value[OptionKey.DifferentBillingAddress]?.value;
      })
    );
  }

  private createBillingAddressDynamicFormFields$() {
    return combineLatest({
      isGoogleMapsApiEnabled: this.#store.select(selectIsGoogleMapsApiEnabled),
      exhibitionSettings: this.#store.select(getExhibitionSettings),
      isUserLoggedIn: this.#store.select(isUserLoggedIn)
    }).pipe(
      first(({ exhibitionSettings }) => !isEmpty(exhibitionSettings)),
      map(({ isGoogleMapsApiEnabled, exhibitionSettings, isUserLoggedIn }) =>
        this.createBillingAddressDynamicFormFields(isGoogleMapsApiEnabled, exhibitionSettings, isUserLoggedIn)
      )
    );
  }

  private createBillingAddressDynamicFormFields(
    isGoogleMapsApiEnabled: boolean,
    exhibitionSettings: ExhibitionSettingModel,
    isUserLoggedIn: boolean
  ) {
    const {
      buyerSettings: { fieldSettings }
    } = exhibitionSettings;

    const dynamicFormFields: DynamicFormFields = [];

    const createDynamicFormFieldMapper = this.#dynamicFormFieldBuilder.createDynamicFormFieldMapper();

    const createDynamicFormFieldParametersMapper = buyerBillingAddressFormDynamicFormFieldParametersMapper(
      isGoogleMapsApiEnabled,
      isUserLoggedIn
    );

    const billingAddressFieldSettings = this.createBillingAddressFieldSettings(fieldSettings);

    forIn(billingAddressFieldSettings, (fieldSetting, fieldKey) => {
      const createDynamicFormFieldFunction = createDynamicFormFieldMapper[fieldKey];
      const createDynamicFormFieldParameters = {
        fieldSetting,
        ...createDynamicFormFieldParametersMapper[fieldKey]
      };

      const dynamicFormField = createDynamicFormFieldFunction(createDynamicFormFieldParameters);

      if (dynamicFormField) {
        dynamicFormFields.push(dynamicFormField);
      }
    });

    return _filter(dynamicFormFields);
  }

  private createBillingAddressFieldSettings(fieldSettings: FieldSettings) {
    return {
      Company: {
        ...fieldSettings.Company,
        isVisible: true,
        order: 1
      },
      Country: {
        ...fieldSettings.Country,
        isVisible: true,
        order: 2
      },
      Address: {
        isMandatory: false,
        isVisible: true,
        order: 3
      },
      Street: {
        ...fieldSettings.Street,
        isVisible: true,
        order: 4
      },
      ZipCode: {
        ...fieldSettings.ZipCode,
        isVisible: true,
        order: 5
      },
      City: {
        ...fieldSettings.City,
        isVisible: true,
        order: 6
      },
      SaveAddress: {
        isMandatory: false,
        isVisible: true,
        order: 7
      }
    };
  }

  private setViewModel(savedBillingAddresses: SavedBillingAddresses) {
    return runInInjectionContext(this.#injector, () => {
      return computed<BuyerBillingAddressFormViewModel>(() => ({
        savedBillingAddresses,
        selectedSavedBillingAddressId: this.#store.selectSignal(selectSelectedSavedBillingAddressId)()
      }));
    });
  }

  private setSavedBillingAddresses$() {
    return this.#store.pipe(
      select(getProfileBillingAddress),
      map((billingAddresses) =>
        !isEmpty(billingAddresses)
          ? reduce(
              billingAddresses,
              (billingAddresses, billingAddress) => {
                const { id, street, city, zipCode } = billingAddress;
                const label = join(
                  _filter([city, street, zipCode], (value) => !isEmpty(value)),
                  ' - '
                );

                billingAddresses.push({
                  id: id || null,
                  label
                });

                return billingAddresses;
              },
              [
                {
                  id: null,
                  label: ''
                }
              ]
            )
          : null
      )
    );
  }

  private setBillingAddressesFieldsVisibility(
    dynamicFormFields: DynamicFormFields,
    formGroup: FormGroup,
    isUserLoggedIn: boolean
  ) {
    const saveAddressDynamicFormField = find(
      dynamicFormFields,
      ({ key }: DynamicFormField) => key === FieldKey.SaveAddress
    );

    if (!isNil(saveAddressDynamicFormField)) {
      saveAddressDynamicFormField.isHidden = !isUserLoggedIn;
      saveAddressDynamicFormField.isDisabled = !isUserLoggedIn;

      const saveBillingAddressFormControl = formGroup.get(`${FieldKey.SaveAddress}.${OptionKey.SaveBillingAddress}`);

      if (!isUserLoggedIn) {
        saveBillingAddressFormControl.setValue(false);
      }

      saveAddressDynamicFormField.isDisabled
        ? saveBillingAddressFormControl.disable()
        : saveBillingAddressFormControl.enable();
    }

    return dynamicFormFields;
  }
}
